// Column definitions and formatting shared by the remittance grids.
//
// FLAT columns, deliberately. AG Grid column groups (`children: [...]`) are not used anywhere else
// in this app and PamGrid registers a fixed module list with no group handling — trying them threw
// `Cannot read properties of null (reading 'getProvidedColumnGroup')` from the group header
// controller. Flat columns also match reconciliation.csv's headers exactly, which is the artifact
// this is held up against, so nothing is lost but the header banding.
//
// Order mirrors the manual Rec tab: identity, then each bucket as a CW / servicer / Diff triplet so
// one row reads as a comparison, then Net, then the reviewer's columns.
import { formatDateTimeForDisplay } from '@1823-partners/core';

// --- Excel formatting, lifted from the manual workbook -----------------------------------------
//
// Read off the `Rec` tab of CML Rec_2026.5.30.xlsx with openpyxl, so the export looks like the
// artifact it replaces rather than approximating it. The scheme is a three-colour header system:
// each bucket appears as a CW / servicer / Diff triplet, coloured by side, with the Diff — the
// answer — in yellow.
//
// The source fills are theme colours plus a 0.4 tint; resolved against that workbook's own
// theme1.xml and flattened to RGB here, because AG Grid's Excel export takes literal colours:
//   identity  theme9 accent6 4EA72E +0.4 -> 95CA82   (green)
//   CW        theme7 accent4 0F9ED5 +0.4 -> 6FC5E6   (blue)
//   servicer  theme8 accent5 A02B93 +0.4 -> C680BE   (magenta)
//   Diff      FFFFFF00                              (yellow, untinted)
export const HEADER_FILL = {
  identity: '95CA82',
  cw: '6FC5E6',
  servicer: 'C680BE',
  diff: 'FFFF00',
};

// The accounting format every money cell on the Rec tab uses. Negatives in parentheses, zeros as a
// dash — the same convention the servicer files arrive in, which is why clean_amount has to undo it
// on the way in.
export const ACCOUNTING_FORMAT = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';

/**
 * AG Grid `excelStyles`. Ids are matched against `cellClass` / `headerClass` on each column, and
 * the reserved id `header` styles every header cell (bold with a thin bottom border, as the manual
 * sheet has); the per-side ids then layer the fill on top.
 */
export const excelStyles = [
  {
    id: 'header',
    font: { bold: true, size: 11 },
    borders: {
      borderBottom: { color: '000000', lineStyle: 'Continuous', weight: 1 },
    },
  },
  ...Object.entries(HEADER_FILL).map(([key, color]) => ({
    id: `remitHeader-${key}`,
    interior: { color: `#${color}`, pattern: 'Solid' },
  })),
  {
    id: 'remitMoney',
    dataType: 'Number',
    numberFormat: { format: ACCOUNTING_FORMAT },
  },
  {
    // The TOTAL row: bold with a top border, as the manual sheet's last row is.
    id: 'remitTotal',
    font: { bold: true },
    borders: { borderTop: { color: '000000', lineStyle: 'Continuous', weight: 1 } },
  },
];

const CURRENCY = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const money = (params) =>
  params.value === null || params.value === undefined || params.value === ''
    ? ''
    : CURRENCY.format(Number(params.value));

/** Cents-level noise is not a discrepancy — the pipeline's own default tolerance. */
export const TOLERANCE = 0.02;

const moneyColumn = (field, headerName, side) => ({
  field,
  headerName,
  type: 'numericColumn',
  valueFormatter: money,
  filter: 'agNumberColumnFilter',
  minWidth: 130,
  // Excel-only: the accounting format and the per-side header fill from the manual Rec tab.
  cellClass: 'remitMoney',
  headerClass: `remitHeader-${side}`,
});

// A Diff outside tolerance is shaded, which is what makes breaks findable without sorting. Both
// directions are marked but distinguished: Clearwater over the servicer is a different finding from
// under, and the sign is the first thing a reviewer reads.
const diffColumn = (field, headerName) => ({
  ...moneyColumn(field, headerName, 'diff'),
  cellClassRules: {
    'remit-diff-over': (p) => Number(p.value) > TOLERANCE,
    'remit-diff-under': (p) => Number(p.value) < -TOLERANCE,
  },
});

/**
 * A boolean column that reads as WORDS, not a checkbox.
 *
 * `cellDataType: false` is the load-bearing part. AG Grid 33 infers a column's data type from the
 * data, and an inferred `boolean` renders with agCheckboxCellRenderer — which ignores
 * `valueFormatter` entirely. The result was a tick-box where the text should be, and on the
 * integrity column that tick sat on a red background, reading as "fine, but angry". These values
 * are read-only facts about a finished run, not toggles, so a checkbox is the wrong affordance
 * twice over: it says nothing and it implies you can click it.
 *
 * The filter list gets 'no' where the cell shows blank — a blank cell is quiet in a grid of 119
 * rows, but a blank entry in a filter dropdown is unreadable.
 */
const flagColumn = (field, headerName, minWidth) => ({
  field,
  headerName,
  minWidth,
  cellDataType: false,
  filter: 'agSetColumnFilter',
  valueFormatter: (p) => (p.value ? 'yes' : ''),
  filterParams: { valueFormatter: (p) => (p.value ? 'yes' : 'no') },
});

const BUCKETS = [
  ['CPN', 'CPN'],
  ['RPMT', 'RPMT'],
  ['EXP', 'EXP'],
  ['INC', 'INC'],
  ['NET', 'Net'],
];

/**
 * Reconciliation columns. `hideEmptyBuckets` lists buckets whose every value is zero for this run;
 * they are hidden rather than dropped. INC was all-zero for May 2026 and NOT for June (6 loans) or
 * July (8, one at 175,648.00), so the data always carries it and only the display hides it.
 */
export const reconciliationColumns = (hideEmptyBuckets = []) => [
  { field: 'CW_ID', headerName: 'CW ID', minWidth: 170, filter: 'agTextColumnFilter', headerClass: 'remitHeader-identity' },
  { headerClass: 'remitHeader-identity', field: 'LOAN_NAME', headerName: 'Loan Name', minWidth: 210, filter: 'agTextColumnFilter' },
  { headerClass: 'remitHeader-identity', field: 'LOAN_ID', headerName: 'Servicer Loan', minWidth: 140, filter: 'agTextColumnFilter' },
  { headerClass: 'remitHeader-identity', field: 'ACCOUNT', headerName: 'Account', minWidth: 190, filter: 'agSetColumnFilter' },
  // 'CLEARWATER only' and 'SERVICER only' are one-sided breaks and read differently from a
  // two-sided disagreement, so this is filterable in one click.
  { headerClass: 'remitHeader-identity', field: 'COVERAGE', headerName: 'Coverage', minWidth: 250, filter: 'agSetColumnFilter' },
  ...BUCKETS.filter(([bucket]) => !hideEmptyBuckets.includes(bucket)).flatMap(([bucket, label]) => [
    moneyColumn(`CW_${bucket}`, `CW ${label}`, 'cw'),
    moneyColumn(`SITUS_${bucket}`, `Servicer ${label}`, 'servicer'),
    diffColumn(`DIFF_${bucket}`, `Diff ${label}`),
  ]),
  flagColumn('FLAGGED', 'Open', 100),
  flagColumn('ACCEPTED', 'Signed off', 125),
  { field: 'NOTE', headerName: 'Note', minWidth: 260, filter: 'agTextColumnFilter', flex: 1 },
];

/** Buckets where every stored value is zero, so the grid can hide their columns. */
export const emptyBuckets = (loans) =>
  // Guarded because [].every() is vacuously TRUE: with no loans yet every bucket looked empty and
  // the grid rendered identity columns only, so the column set flickered into place on load.
  !loans || !loans.length
    ? []
    : BUCKETS.map(([bucket]) => bucket).filter((bucket) =>
      loans.every((row) =>
        ['CW', 'SITUS', 'DIFF'].every((side) => !Number(row[`${side}_${bucket}`]))
      )
    );

/**
 * The TOTAL row, as a pinned bottom row rather than a data row — a data row would sort like a loan
 * and vanish under a filter.
 *
 * Read it with care. `DIFF_NET` is the most misleading number in this reconciliation: for May 2026
 * one in-scope loan the manual recon omits was ~81% of the whole gap, and from June the ten CPTMG
 * tranches contribute ~$3.3M of Clearwater `Buy` activity the servicer never reports — real, known,
 * and not a break. That is why the dashboard shows the largest contributors beside it.
 */
export const totalsRow = (loans) => {
  if (!loans.length) return [];
  const row = { CW_ID: 'TOTAL', LOAN_NAME: '', LOAN_ID: '', ACCOUNT: '', COVERAGE: '' };
  BUCKETS.forEach(([bucket]) => {
    ['CW', 'SITUS', 'DIFF'].forEach((side) => {
      const field = `${side}_${bucket}`;
      row[field] = loans.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
    });
  });
  return [row];
};

/** The loans driving a column total — what turns a misleading number into an informative one. */
export const topContributors = (loans, field = 'DIFF_NET', limit = 4) =>
  [...loans]
    .filter((row) => Math.abs(Number(row[field]) || 0) > TOLERANCE)
    .sort((a, b) => Math.abs(Number(b[field]) || 0) - Math.abs(Number(a[field]) || 0))
    .slice(0, limit);

/**
 * Legend for the reconciliation grid, in PamGrid's `legendItems` shape.
 *
 * It exists because the conventions are not self-evident: the `Open` and `Signed off` columns are
 * real and labelled, but they sit to the RIGHT of fifteen money columns, so while you are reading
 * the left of a row you cannot see them. The row markers carry that state to where your eye is —
 * and an unlabelled marker is a marker nobody can read, hence this.
 */
export const RECONCILIATION_LEGEND = [
  { color: 'rgba(248, 113, 113, 0.9)', label: 'Open — needs review' },
  { color: 'rgba(148, 163, 184, 0.95)', label: 'Signed off — Diff still shown, not counted as open' },
  { color: 'rgba(248, 113, 113, 0.18)', label: 'Diff: Clearwater under the servicer' },
  { color: 'rgba(52, 211, 153, 0.18)', label: 'Diff: Clearwater over the servicer' },
];

/**
 * The stored servicer files for one month.
 *
 * Every red rule here marks the same failure -- input the engine will not read -- one step before
 * a run reports it as an integrity count. A file with no rows contributes no cash at all, and it
 * does so silently: the upload succeeded, the file is listed, and the month simply reconciles
 * against less than it should. Flagging it in this list is the only place that is cheap to notice.
 *
 * `ROWS_PARSED` is flagged on falsy rather than `=== 0`: a stored row whose parse failed carries
 * null there, and null and zero mean the same thing to a run.
 */
export const filesColumns = () => [
  { field: 'FILE_NAME', headerName: 'File', minWidth: 260, flex: 1 },
  {
    field: 'STREAM',
    headerName: 'Stream',
    minWidth: 140,
    filter: 'agSetColumnFilter',
    // 'unrecognized' is not a servicer; it is the parser saying it read nothing.
    cellClassRules: { 'remit-integrity-bad': (p) => p.value === 'unrecognized' },
  },
  {
    field: 'ROWS_PARSED',
    headerName: 'Rows parsed',
    type: 'numericColumn',
    minWidth: 130,
    cellClassRules: { 'remit-integrity-bad': (p) => !p.value },
  },
  {
    field: 'PARSE_ERROR',
    headerName: 'Parse error',
    minWidth: 240,
    cellClassRules: { 'remit-integrity-bad': (p) => !!p.value },
  },
  { field: 'UPLOADED_BY', headerName: 'Uploaded by', minWidth: 200 },
  {
    field: 'MODIFIED_DATE',
    headerName: 'Stored at',
    minWidth: 175,
    valueFormatter: (p) => (p.value ? formatDateTimeForDisplay(p.value) : ''),
  },
];
